import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Card, Input, aiAccessErrorMessage } from '@copilot/ui';
import { EmptyState, Modal } from '@copilot/ui';
import Icon from '../components/common/Icon';
import { Field } from '../components/common/Field';
import DocumentList from '../components/documents/DocumentList';
import DocumentViewer from '../components/documents/DocumentViewer';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import * as documentService from '../services/documentService';
import { reportError } from '../services/errorTracking';
import {
  DOCUMENT_ACCEPT,
  DOCUMENT_EXTENSIONS,
  DOCUMENT_UNSUPPORTED_MESSAGE,
  extensionOf,
} from '../utils/documentTypes';
import { getUserFriendlyError } from '../utils/errors';

// Mirrors backend/schemas/document.py's ManualDocumentCreate title limit.
const MAX_TITLE_LENGTH = 255;

/**
 * The AI document library, structurally ported from
 * desktop/src/pages/DocumentsPage.tsx: one header with Refresh/Add
 * Manual/Upload File actions, one library card, no permanently visible
 * upload/manual forms. Both clients read and write the exact same
 * backend documents (GET/POST/DELETE /api/documents...) - only the
 * transport differs, and only because it must: this page never opens a
 * native file dialog, it drives a normal hidden <input type="file">
 * through documentService.js/httpClient, the same way every other web
 * upload in this app works.
 */
function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualTitle, setManualTitle] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [manualError, setManualError] = useState('');

  const [documentPendingDelete, setDocumentPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Held by id and resolved against the list below, so an edit's new
  // title and its re-processing status reach the open viewer as the
  // list reloads and polls.
  const [viewedDocumentId, setViewedDocumentId] = useState(null);

  const fileInputRef = useRef(null);

  const viewedDocument = useMemo(
    () => documents.find((document) => document.id === viewedDocumentId) || null,
    [documents, viewedDocumentId]
  );

  // Same shape as desktop's hasPendingDocuments: true while anything is
  // still queued or being processed.
  const hasPendingDocuments = useMemo(
    () =>
      documents.some((document) =>
        ['pending', 'processing'].includes(document.processing_status)
      ),
    [documents]
  );

  const loadDocuments = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    setError('');

    try {
      const data = await documentService.getDocuments();
      setDocuments(data);
    } catch (err) {
      reportError('documents_load', err);
      setError(getUserFriendlyError(err, 'Could not load documents.'));
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments(true);
  }, [loadDocuments]);

  // Polls every 5s while something is still pending/processing, exactly
  // like desktop/src/pages/DocumentsPage.tsx - and stops the instant
  // nothing is left in flight, rather than polling forever.
  useEffect(() => {
    if (!hasPendingDocuments) return undefined;
    const timer = window.setInterval(() => loadDocuments(), 5000);
    return () => window.clearInterval(timer);
  }, [hasPendingDocuments, loadDocuments]);

  // Silent re-read: no spinner, no error banner, last good list kept on
  // failure. Only the newest call may apply its result. While documents
  // are pending/processing the 5s poll above already covers this, so the
  // generic refresh stays off then rather than doubling the requests.
  const refreshSeqRef = useRef(0);
  const refreshDocuments = useCallback(async ({ isStale = () => false } = {}) => {
    const seq = ++refreshSeqRef.current;
    const data = await documentService.getDocuments();
    if (!isStale() && seq === refreshSeqRef.current) setDocuments(data);
  }, []);

  useAutoRefresh(refreshDocuments, { enabled: !hasPendingDocuments && !isUploading });

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(event) {
    const files = Array.from(event.target.files || []);
    // Cleared immediately (not just on success) so picking the SAME
    // file(s) again still fires a change event.
    event.target.value = '';
    if (files.length === 0) return;

    setError('');

    // Unsupported files are skipped (and named below); the rest still
    // upload rather than one bad pick blocking the whole selection.
    const failures = [];
    const uploadable = files.filter((file) => {
      if (DOCUMENT_EXTENSIONS.includes(extensionOf(file.name))) return true;
      failures.push({ name: file.name, message: DOCUMENT_UNSUPPORTED_MESSAGE });
      return false;
    });

    if (uploadable.length > 0) {
      setIsUploading(true);
      setIsBusy(true);

      try {
        // One at a time, so a large selection doesn't open a burst of
        // parallel uploads against the backend's per-user limits.
        for (const file of uploadable) {
          try {
            await documentService.uploadDocument(file);
          } catch (err) {
            reportError('document_upload', err);
            // A plan/Meeting Pass refusal is named, not reported as a
            // failure - and applies to every remaining file, so stop.
            const accessMessage = aiAccessErrorMessage(err);
            if (accessMessage) {
              failures.length = 0;
              failures.push({ name: file.name, message: accessMessage, access: true });
              break;
            }
            failures.push({
              name: file.name,
              message: getUserFriendlyError(err, 'Could not upload document.'),
            });
          }
        }
        // Reload from the backend rather than prepending the responses -
        // processing_status is the backend's to own, and a reload keeps
        // the polling effect above working from a correctly ordered,
        // authoritative list rather than a guessed one. Reloaded even
        // after a failure, since earlier files may have uploaded.
        await loadDocuments();
      } finally {
        setIsUploading(false);
        setIsBusy(false);
      }
    }

    if (failures.length === 1 && (files.length === 1 || failures[0].access)) {
      setError(failures[0].message);
    } else if (failures.length > 0) {
      setError(
        `${failures.length} of ${files.length} files could not be uploaded. ` +
          failures.map(({ name, message }) => `${name}: ${message}`).join(' ')
      );
    }
  }

  function openManualModal() {
    setManualError('');
    setManualOpen(true);
  }

  function closeManualModal() {
    if (isSavingManual) return;
    setManualOpen(false);
    setManualError('');
  }

  async function handleCreateManualDocument(event) {
    event?.preventDefault();

    const trimmedTitle = manualTitle.trim();
    const trimmedContent = manualContent.trim();

    if (!trimmedTitle) {
      setManualError('Title is required.');
      return;
    }

    if (!trimmedContent) {
      setManualError('Content is required.');
      return;
    }

    // Guards a double-click firing two creates while the first request
    // is still in flight - Save Document is also disabled below, but
    // this makes the guard correct even without that.
    if (isSavingManual) return;

    setIsSavingManual(true);
    setManualError('');

    try {
      await documentService.createManualDocument({
        title: trimmedTitle,
        content: trimmedContent,
      });
      setManualTitle('');
      setManualContent('');
      setManualOpen(false);
      await loadDocuments();
    } catch (err) {
      reportError('manual_document_create', err);
      setManualError(aiAccessErrorMessage(err) || getUserFriendlyError(err, 'Could not create document.'));
    } finally {
      setIsSavingManual(false);
    }
  }

  function handleDeleteRequest(document) {
    setDeleteError('');
    setDocumentPendingDelete(document);
  }

  function closeDeleteModal() {
    if (isDeleting) return;
    setDocumentPendingDelete(null);
    setDeleteError('');
  }

  async function handleConfirmDelete() {
    if (!documentPendingDelete) return;

    setIsDeleting(true);
    setDeleteError('');

    try {
      await documentService.deleteDocument(documentPendingDelete.id);
      setDocuments((current) =>
        current.filter((doc) => doc.id !== documentPendingDelete.id)
      );
      setDocumentPendingDelete(null);
      refreshDocuments().catch(() => {});
    } catch (err) {
      reportError('document_delete', err);
      setDeleteError(
        getUserFriendlyError(err, 'Unable to delete document. Please try again.')
      );
      // Reconciles with the backend rather than trusting the optimistic
      // remove that never happened - a delete can fail after partially
      // applying server-side (e.g. a concurrent change).
      await loadDocuments();
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="command-dashboard documents-page">
      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        multiple
        style={{ display: 'none' }}
        aria-label="Upload document"
        onChange={handleFileSelected}
      />

      <header className="command-header">
        <div>
          <span className="command-eyebrow">Documents</span>
          <h1>AI document library</h1>
          <p>Synced with your KrishvaQ account and available for meeting context.</p>
        </div>
        <div className="command-header-actions">
          <Button
            variant="secondary"
            onClick={() => loadDocuments(true)}
            disabled={isBusy || isLoading}
          >
            <Icon name="refresh" size={16} />
            Refresh
          </Button>
          <Button variant="secondary" onClick={openManualModal} disabled={isBusy}>
            <Icon name="plus" size={16} />
            Add Manual
          </Button>
          <Button onClick={openFilePicker} disabled={isBusy}>
            <Icon name="upload" size={16} />
            {isUploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </div>
      </header>

      {error && <div className="form-alert">{error}</div>}

      <Card interactive className="documents-library-card">
        {isLoading ? (
          <p className="backend-document-meta">Loading documents...</p>
        ) : documents.length === 0 ? (
          <EmptyState
            icon={<Icon name="file-text" size={30} />}
            title="No documents yet."
            description="Upload resumes, requirements, notes, or other context you want KrishvaQ to use in meetings."
            action={
              <Button onClick={openFilePicker} disabled={isBusy}>
                <Icon name="upload" size={16} />
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            }
          />
        ) : (
          <DocumentList
            documents={documents}
            onView={(document) => setViewedDocumentId(document.id)}
            onDelete={handleDeleteRequest}
            deleteDisabled={isBusy || isDeleting}
          />
        )}
      </Card>

      {manualOpen && (
        <Modal
          title="Add manual document"
          onClose={closeManualModal}
          actions={
            <>
              <Button variant="secondary" onClick={closeManualModal} disabled={isSavingManual}>
                Cancel
              </Button>
              <Button onClick={handleCreateManualDocument} disabled={isSavingManual}>
                {isSavingManual ? 'Saving...' : 'Save Document'}
              </Button>
            </>
          }
        >
          <form className="manual-document-form" onSubmit={handleCreateManualDocument}>
            <Field label="Title" htmlFor="documents-manual-title">
              <Input
                id="documents-manual-title"
                value={manualTitle}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(event) => setManualTitle(event.target.value)}
                disabled={isSavingManual}
              />
            </Field>

            <Field label="Content" htmlFor="documents-manual-content">
              <textarea
                id="documents-manual-content"
                className="input manual-document-textarea"
                value={manualContent}
                onChange={(event) => setManualContent(event.target.value)}
                disabled={isSavingManual}
              />
            </Field>

            {manualError && <div className="form-alert">{manualError}</div>}
          </form>
        </Modal>
      )}

      {viewedDocument && (
        <DocumentViewer
          document={viewedDocument}
          onClose={() => setViewedDocumentId(null)}
          onSaved={() => loadDocuments()}
        />
      )}

      {documentPendingDelete && (
        <Modal
          title="Delete document?"
          onClose={closeDeleteModal}
          actions={
            <>
              <Button variant="secondary" onClick={closeDeleteModal} disabled={isDeleting}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </>
          }
        >
          {deleteError ? (
            <div className="form-alert">{deleteError}</div>
          ) : (
            <>
              This removes <strong>{documentPendingDelete.file_name}</strong> from your
              synced AI documents.
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

export default DocumentsPage;
